/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Whether the rule names this resource or every resource of its kind.
 */
export const ResourceAudienceEntryAppliesTo = {
  Resource: "resource",
  AllResources: "all_resources",
} as const;
/**
 * Whether the rule names this resource or every resource of its kind.
 */
export type ResourceAudienceEntryAppliesTo = OpenEnum<
  typeof ResourceAudienceEntryAppliesTo
>;

export const ResourceAudienceEntryDispositions = {
  ReadOnly: "read_only",
  Destructive: "destructive",
  Idempotent: "idempotent",
  OpenWorld: "open_world",
} as const;
export type ResourceAudienceEntryDispositions = OpenEnum<
  typeof ResourceAudienceEntryDispositions
>;

/**
 * What the principal identifies.
 */
export const ResourceAudienceEntryKind = {
  Everyone: "everyone",
  Role: "role",
  User: "user",
  Agent: "agent",
  DirectoryGroup: "directory_group",
  DirectoryAttribute: "directory_attribute",
  Unknown: "unknown",
} as const;
/**
 * What the principal identifies.
 */
export type ResourceAudienceEntryKind = OpenEnum<
  typeof ResourceAudienceEntryKind
>;

/**
 * Access this principal has on the resource, or the access a rule takes away.
 */
export const ResourceAudienceEntryLevel = {
  Use: "use",
  View: "view",
  Manage: "manage",
  Blocked: "blocked",
  BlockedView: "blocked_view",
  BlockedManage: "blocked_manage",
} as const;
/**
 * Access this principal has on the resource, or the access a rule takes away.
 */
export type ResourceAudienceEntryLevel = OpenEnum<
  typeof ResourceAudienceEntryLevel
>;

export type ResourceAudienceEntry = {
  /**
   * Ids of the agents this rule currently reaches, whether it names them or a role they hold.
   */
  agentIds?: Array<string> | undefined;
  /**
   * Whether the rule names this resource or every resource of its kind.
   */
  appliesTo: ResourceAudienceEntryAppliesTo;
  /**
   * Secondary line: email, member count, or attribute key.
   */
  description?: string | undefined;
  /**
   * Human-readable name for the principal.
   */
  displayName: string;
  /**
   * Tool annotations the rule is narrowed to, when it is not the whole resource.
   */
  dispositions?: Array<ResourceAudienceEntryDispositions> | undefined;
  /**
   * What the principal identifies.
   */
  kind: ResourceAudienceEntryKind;
  /**
   * Access this principal has on the resource, or the access a rule takes away.
   */
  level: ResourceAudienceEntryLevel;
  /**
   * How many people the principal reaches, when known.
   */
  memberCount?: number | undefined;
  /**
   * User ids of the organization members this rule currently reaches.
   */
  memberIds?: Array<string> | undefined;
  /**
   * Canonical principal URN this rule belongs to.
   */
  principalUrn: string;
  /**
   * Tool names the rule is narrowed to, when it is not the whole resource.
   */
  tools?: Array<string> | undefined;
};

/** @internal */
export const ResourceAudienceEntryAppliesTo$inboundSchema: z.ZodMiniType<
  ResourceAudienceEntryAppliesTo,
  unknown
> = openEnums.inboundSchema(ResourceAudienceEntryAppliesTo);

/** @internal */
export const ResourceAudienceEntryDispositions$inboundSchema: z.ZodMiniType<
  ResourceAudienceEntryDispositions,
  unknown
> = openEnums.inboundSchema(ResourceAudienceEntryDispositions);

/** @internal */
export const ResourceAudienceEntryKind$inboundSchema: z.ZodMiniType<
  ResourceAudienceEntryKind,
  unknown
> = openEnums.inboundSchema(ResourceAudienceEntryKind);

/** @internal */
export const ResourceAudienceEntryLevel$inboundSchema: z.ZodMiniType<
  ResourceAudienceEntryLevel,
  unknown
> = openEnums.inboundSchema(ResourceAudienceEntryLevel);

/** @internal */
export const ResourceAudienceEntry$inboundSchema: z.ZodMiniType<
  ResourceAudienceEntry,
  unknown
> = z.pipe(
  z.object({
    agent_ids: z.optional(z.array(z.string())),
    applies_to: ResourceAudienceEntryAppliesTo$inboundSchema,
    description: z.optional(z.string()),
    display_name: z.string(),
    dispositions: z.optional(
      z.array(ResourceAudienceEntryDispositions$inboundSchema),
    ),
    kind: ResourceAudienceEntryKind$inboundSchema,
    level: ResourceAudienceEntryLevel$inboundSchema,
    member_count: z.optional(z.int()),
    member_ids: z.optional(z.array(z.string())),
    principal_urn: z.string(),
    tools: z.optional(z.array(z.string())),
  }),
  z.transform((v) => {
    return remap$(v, {
      "agent_ids": "agentIds",
      "applies_to": "appliesTo",
      "display_name": "displayName",
      "member_count": "memberCount",
      "member_ids": "memberIds",
      "principal_urn": "principalUrn",
    });
  }),
);

export function resourceAudienceEntryFromJSON(
  jsonString: string,
): SafeParseResult<ResourceAudienceEntry, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ResourceAudienceEntry$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ResourceAudienceEntry' from JSON`,
  );
}
